import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { defaultSettings, type AppSettings } from '@/config/defaults'
import { ErrorHandler } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'

const SETTINGS_STORAGE_KEY = 'bibleawr-app-settings'

/**
 * Store для налаштувань додатку
 * Зберігає налаштування в localStorage
 */
export const useAppSettingsStore = defineStore('appSettings', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('appSettings')
  // State
  const settings = ref<AppSettings>(loadSettingsFromStorage())
  const loading = ref(false)
  const isInitialized = ref(false)

  // Getters
  const isLoading = computed(() => loading.value)
  const appVersion = computed(() => settings.value.application_version)
  const appLanguage = computed(() => settings.value.application_lang)
  const appTheme = computed(() => settings.value.application_theme)

  /**
   * Завантажити налаштування з localStorage
   */
  function loadSettingsFromStorage(): AppSettings {
    if (!process.client) {
      return { ...defaultSettings }
    }

    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<AppSettings>
        // Мержимо з дефолтними, щоб гарантувати всі поля
        return {
          ...defaultSettings,
          ...parsed,
        }
      }
    } catch (err) {
      console.error('Failed to load settings from localStorage:', err)
    }

    return { ...defaultSettings }
  }

  /**
   * Зберегти налаштування в localStorage
   */
  function saveSettingsToStorage(newSettings: AppSettings) {
    if (!process.client) {
      return
    }

    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(newSettings))
    } catch (err) {
      console.error('Failed to save settings to localStorage:', err)
      throw err
    }
  }

  /**
   * Завантажити налаштування (з localStorage)
   */
  function loadSettings() {
    loading.value = true
    clearErrors()

    try {
      const loaded = loadSettingsFromStorage()
      settings.value = loaded
      isInitialized.value = true
      return loaded
    } catch (err) {
      const handledError = err instanceof Error ? err : new Error('Failed to load settings')
      setApiError(ErrorHandler.toSerializable(handledError))
      settings.value = { ...defaultSettings }
      throw handledError
    } finally {
      loading.value = false
    }
  }

  /**
   * Оновити налаштування
   */
  function updateSettings(newSettings: Partial<AppSettings>) {
    loading.value = true
    clearErrors()

    try {
      const updated = {
        ...settings.value,
        ...newSettings,
      }
      settings.value = updated
      // Зберігаємо напряму, watch не потрібен тут, бо ми вже зберігаємо
      saveSettingsToStorage(updated)
      return updated
    } catch (err) {
      const handledError = err instanceof Error ? err : new Error('Failed to update settings')
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  /**
   * Скинути налаштування до дефолтних
   */
  function resetSettings() {
    settings.value = { ...defaultSettings }
    if (process.client) {
      localStorage.removeItem(SETTINGS_STORAGE_KEY)
    }
    isInitialized.value = false
    clearErrors()
  }

  // Синхронізуємо зміни налаштувань з localStorage
  // Watch потрібен для випадків, коли налаштування змінюються напряму через settings.value
  watch(
    settings,
    (newSettings) => {
      if (isInitialized.value && process.client) {
        // Перевіряємо, чи налаштування дійсно змінилися
        const current = loadSettingsFromStorage()
        const hasChanges = JSON.stringify(current) !== JSON.stringify(newSettings)
        if (hasChanges) {
          saveSettingsToStorage(newSettings)
        }
      }
    },
    { deep: true }
  )

  return {
    // State
    settings,
    loading: isLoading,
    error,
    isInitialized,

    // Getters
    hasError,
    appVersion,
    appLanguage,
    appTheme,

    // Actions
    loadSettings,
    updateSettings,
    resetSettings,
  }
})
